import UserIcon from "@mui/icons-material/AccountCircle";
import AIIcon from "@mui/icons-material/Computer";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Box, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MessageNote } from "./message-note";
import { body1ResponsiveSizes } from "../../../shared/styles/custom-styles";
import { FeedbackButtons } from "../../feedback";
import { ReportSuggestionsList } from "../../report";
import { Message } from "../types";
import { MarkdownRenderer } from "./markdown-renderer";
import { DaxQueryDisplay } from "./dax-query-display";
import { PBIDataTable } from "./pbi-data-table";
import { useState } from "react";

type MessageCardProps = {
  message: Message;
};

export const MessageCard = ({ message }: MessageCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isError = message.sender === "ai" && message.isError;
  const isAutoGenerated = message.sender === "user" && message.isAutoGenerated;
  const avatarBgColor = message.sender === "ai" ? "info.main" : "primary.main";
  const cardBgColor = isError
    ? "#ffcdd2"
    : message.sender === "ai"
    ? "info.main"
    : "primary.main";
  const textColor = isError
    ? "#c62828"
    : message.sender === "ai"
    ? "info.contrastText"
    : "secondary.contrastText";
  const showFeedback = message.sender === "ai" && !isError;
  const isReport = message.sender === "ai" && message.isReport;
  // Check if DAX and PBI data are present in the response (backend controls this)
  const hasStructuredData =
    message.sender === "ai" &&
    !message.isReport &&
    (message.daxQuery || message.pbiTable);
  const isDataOnlyMessage = hasStructuredData && !message.text;

  // Don't render data-only messages when no structured data is present
  if (!hasStructuredData && !message.text && !isReport) {
    return null;
  }

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      width="100%"
      flexDirection={message.sender === "user" ? "row-reverse" : "row"}
    >
      <Avatar sx={{ bgcolor: avatarBgColor, marginRight: 2 }}>
        {message.sender === "user" ? (
          <UserIcon sx={{ color: "main.contrastText" }} />
        ) : (
          <AIIcon sx={{ color: "info.contrastText" }} />
        )}
      </Avatar>
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: "80%", md: "60%", lg: "60%", xl: "60%" },
        }}
      >
        <Card
          sx={{
            bgcolor: cardBgColor,
            color: textColor,
            borderRadius: "20px",
            margin: "8px",
            padding: "0px 10px",
            wordBreak: "break-word",
          }}
        >
          <CardContent>
            {isAutoGenerated && <MessageNote text={"Auto-generated"} />}

            {/* Data-only message: DAX Query and Table - Collapsible */}
            {isDataOnlyMessage ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    mb: 1,
                  }}
                  onClick={handleToggleExpand}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: textColor }}
                  >
                    📊 DAX Query & PBI Data
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ color: textColor }}
                    aria-label={isExpanded ? "collapse" : "expand"}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {message.daxQuery && (
                    <DaxQueryDisplay daxQuery={message.daxQuery} textColor={textColor} />
                  )}
                  {message.pbiTable && (
                    <PBIDataTable data={message.pbiTable} textColor={textColor} />
                  )}
                </Collapse>
                {!isExpanded && (
                  <Typography variant="caption" sx={{ color: textColor, opacity: 0.7 }}>
                    Click to view DAX query and PBI data table
                  </Typography>
                )}
              </>
            ) : isReport ? (
              <>
                <MessageNote text={"Report Suggestions"} ai={true} />
                <ReportSuggestionsList
                  text={message.text}
                  reports={message.reports}
                />
              </>
            ) : (
              <Box
                sx={{
                  marginTop: isAutoGenerated ? "8px" : "0",
                  fontSize: body1ResponsiveSizes,
                  '& p': { margin: 0, marginBottom: '8px', ':last-of-type': { marginBottom: 0 } },
                }}
              >
                <MarkdownRenderer
                  text={message.text}
                  fontSize={body1ResponsiveSizes}
                  color={textColor}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      {!isReport && !isDataOnlyMessage && message.sender === "ai" && (
        <FeedbackButtons
          showFeedback={showFeedback}
          messageId={message.messageId ?? undefined}
        />
      )}
    </Box>
  );
};
